import { getJson, postJson, patchJson, deleteJson, ApiError } from './authApi';
export { ApiError };
export async function adminLogin(login, password) {
    return postJson('/api/admin/login', { login, password });
}
export async function getAdminSession() {
    return getJson('/api/admin/session');
}
export async function adminLogout() {
    await fetch('/api/admin/logout', { method: 'POST' });
}
export async function fetchPlaceTypes() {
    const data = await getJson('/api/admin/place-types');
    return data.placeTypes;
}
export async function fetchSubscriptionPlans() {
    const data = await getJson('/api/admin/subscription-plans');
    return data.plans;
}
export async function createSubscriptionPlan(input) {
    return postJson('/api/admin/subscription-plans', input);
}
export async function updateSubscriptionPlan(id, input) {
    return patchJson('/api/admin/subscription-plans', { id, ...input });
}
export async function deleteSubscriptionPlan(id) {
    return deleteJson('/api/admin/subscription-plans', { id });
}
export async function fetchHourlyRates() {
    const data = await getJson('/api/admin/hourly-rates');
    return data.places;
}
export async function updateHourlyRate(id, prices) {
    return patchJson('/api/admin/hourly-rates', { id, ...prices });
}
export async function fetchWorkingHours() {
    const data = await getJson('/api/admin/working-hours');
    return data.days;
}
export async function updateWorkingHours(days) {
    return patchJson('/api/admin/working-hours', { days });
}
export async function fetchSpecialDays() {
    const data = await getJson('/api/admin/special-days');
    return data.days;
}
export async function saveSpecialDays(days) {
    return postJson('/api/admin/special-days', { days });
}
export async function fetchCustomers() {
    const data = await getJson('/api/admin/customers');
    return data.customers;
}
export async function fetchCustomerSubscriptions(userId) {
    const data = await getJson(`/api/admin/customer-subscriptions?userId=${userId}`);
    return data.subscriptions;
}
export async function sellSubscription(userId, planId) {
    return postJson('/api/admin/sell-subscription', { userId, planId });
}
// --- Deduct time ------------------------------------------------------------
// Deducts `minutes` (≥ 60, in 15-minute steps) from one of the customer's
// subscriptions and returns the resulting balance. The server re-checks the
// balance, so this can fail with an `insufficient_balance` code.
export async function deductTime(userId, subscriptionId, minutes) {
    return postJson('/api/admin/deduct-time', { userId, subscriptionId, minutes });
}
// --- Add time ----------------------------------------------------------------
// Adds `minutes` (a positive integer) to one of the customer's subscriptions
// and returns the resulting balance. Sends confirmation emails to both the
// admin and the customer.
export async function addTime(userId, subscriptionId, minutes) {
    return postJson('/api/admin/add-time', { userId, subscriptionId, minutes });
}
// `from`/`to` are inclusive `YYYY-MM-DD` calendar days; `lang` selects which
// localized place name the server returns.
export async function fetchCustomerBookings(userId, from, to, lang) {
    const params = new URLSearchParams({ userId: String(userId), from, to, lang });
    const data = await getJson(`/api/admin/customer-bookings?${params.toString()}`);
    return data.bookings;
}
// `from`/`to` are inclusive `YYYY-MM-DD` calendar days (typically one visible
// month) — the dates within that span that have at least one booking, for the
// calendar's per-date dot marker.
export async function fetchBookingDates(from, to) {
    const params = new URLSearchParams({ from, to });
    const data = await getJson(`/api/admin/bookings/dates?${params.toString()}`);
    return data.dates;
}
// Every booking (any customer) on one `YYYY-MM-DD` calendar day; `lang`
// selects which localized place name the server returns.
export async function fetchBookingsByDate(date, lang) {
    const params = new URLSearchParams({ date, lang });
    const data = await getJson(`/api/admin/bookings/by-date?${params.toString()}`);
    return data.bookings;
}
export async function fetchRescheduleAvailability(bookingId) {
    const params = new URLSearchParams({ bookingId: String(bookingId) });
    return getJson(`/api/admin/bookings/reschedule-availability?${params.toString()}`);
}
// Moves a booking to a new interval on the same day. The server re-checks the
// free windows and the subscription balance, so this can fail with a
// `slot_unavailable` / `insufficient_balance` / `subscription_expired` /
// `booking_past` / `already_cancelled` code. Emails both the admin and the customer.
export async function rescheduleBooking(bookingId, startMinutes, endMinutes) {
    return postJson('/api/admin/bookings/reschedule', { bookingId, startMinutes, endMinutes });
}
// Cancels a booking on the customer's behalf (no 24h lead-time rule). A
// subscription booking's minutes go back to its subscription (`refundedMinutes`
// is null for an hourly one). Can fail with `already_cancelled` /
// `booking_past`. Emails both the admin and the customer.
export async function cancelBookingByAdmin(bookingId) {
    return postJson('/api/admin/bookings/cancel', { bookingId });
}
export async function fetchNewBookingAvailability(userId, placeId, date) {
    const params = new URLSearchParams({ userId: String(userId), placeId: String(placeId), date });
    return getJson(`/api/admin/bookings/new-availability?${params.toString()}`);
}
// Books a slot for a customer (no 24h lead-time rule). Paid from a
// subscription with enough time left, otherwise hourly. Can fail with
// `slot_unavailable` / `day_closed` / `no_payment_option`. Sends the same
// emails as a booking the customer made themselves.
export async function createBookingForCustomer(input) {
    return postJson('/api/admin/bookings/create', input);
}
// `deleted: true` lists soft-deleted customers instead of active ones (the
// "Show deleted users" toggle on the Clients screen).
export async function fetchClients(deleted = false) {
    const data = await getJson(`/api/admin/clients${deleted ? '?deleted=1' : ''}`);
    return data.clients;
}
// Soft-deletes a customer: they stop appearing in every admin dropdown
// (sell/remaining-hours/booking-history), but their row, subscriptions and
// booking history are untouched.
export async function deleteClient(userId) {
    return deleteJson('/api/admin/clients', { userId });
}
// Clears a customer's soft-delete: they reappear in every admin dropdown.
export async function restoreClient(userId) {
    return patchJson('/api/admin/clients', { userId });
}
